import {customElement, property, state} from "lit/decorators.js";
import {LitElement, html, CSSResult, unsafeCSS} from "lit";
import {provide} from '@lit/context';
import {
  OnlineReservationConfig,
  Messages,
  Store,
} from "../../@types/online-reservation.js";

import '../tooltip/tooltip.js';

import styles from './online-reservation.scss';
import {OpenHvaModalEvent} from "../../@types/dom.js";
import { i18nContext } from "./i18nContext.js";
import { AppState, ModelState, SelectionState } from "./states/index.js";
import arrowBackIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_arrow-left.svg';
import {addPresentationToIcon} from "../icon/icon-accessibility.js";
import { track } from '../tracking/generic-tracking-layer.js';
import {ORInformation} from './content/or-information.js';
import {ORSize} from './content/or-size.js';
import {ORStore} from './content/or-store.js';
import {
  createInitialStepFourState,
  validateEmailValue,
  StepFourState,
  ORForm,
  ORFormParams
} from './content/or-form.js';
import {EntsDrawer} from "../drawer/drawer.js";
import {DomainEvent} from "@cube/tracking-library-api";
import {OnlineReservationResponseDTO} from "./states/model-state.js";
import {ORResult} from "./content/or-result.js";

@customElement('ents-online-reservation')
export class OnlineReservation extends LitElement {

  @property({type: Object, reflect: false}) config: OnlineReservationConfig = {
    articleDetailsUrl: '',
    messages: {},
    dataProtectionRegulationLink: '',
    reservationEndpoint: ''
  };

  @provide({context: i18nContext})
  private messages!: Messages;

  @state() private appState = new AppState();

  @state() private modelState = new ModelState();

  @state() private selectionState = new SelectionState();

  @state() private stepFourState: StepFourState = createInitialStepFourState();


  constructor() {
    super();
    this.messages = this.config.messages;
  }

  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('ents:hva-modal:open', this.handleOpenEvent.bind(this));
    document.addEventListener('onlineReservationDrawer-drawer:close', this.handleCloseEvent.bind(this));
    this.messages = this.config.messages;
  }

  private handleOpenEvent(event: OpenHvaModalEvent) {
    this.loadModel(event.detail.articleId);
    this.showDrawer();
  }

  private handleCloseEvent() {
    this.resetState();
  }

  private loadModel(variantId: string) {
    this.appState = this.appState.setLoading();

    const articleDetailsUrl = this.config.articleDetailsUrl.replace('VARIANT_ID', variantId);
    fetch(articleDetailsUrl)
      .then(response => response.json())
      .then(this.handleModelLoadSuccess.bind(this))
      .catch(() => {
        this.appState = this.appState.setLoadedWithError();
      });
  }

  private handleModelLoadSuccess(response: OnlineReservationResponseDTO) {
    this.modelState = ModelState.createFromResponse(response);
    this.appState = this.appState.setLoaded();

    // Skip size selection when OneSize or NOSize, then take only size available
    if (this.modelState.shouldSkipToStoreSelection()) {
      const firstSizeId = this.modelState.getFirstAvailableSize();
      if (firstSizeId) {
        this.selectionState = this.selectionState.selectSize(firstSizeId);
      }
    }
  }

  render() {
    return html`
      <ents-drawer id="onlineReservationDrawer" class="ents-or__drawer" full-screen>
        ${this.renderContent()}
      </ents-drawer>
    `;
  }

  private renderBackButton() {
    if (this.appState.currentStep === 0 || this.appState.currentStep === 4) {
      return '';
    }

    return html`
      <div slot="header-start">
        <a
          slot="header-start"
          class="bdk-link-secondary"
          size="m"
          href="#"
          data-test-ents-or-back-button
          aria-label="${this.messages['back']}"
          @click="${this.handleBackNavigation}">
          <div>
            ${addPresentationToIcon(arrowBackIcon())}
          </div>
          ${this.messages['back']}
        </a>
      </div>
    `;
  }

  private readonly handleBackNavigation = () => {
    const { skipSizeLayer } = this.modelState;
    if (this.appState.currentStep === 2 && skipSizeLayer) {
      // Without size selection, go directly to the first step
      this.appState = this.appState.goToFirstStep();
    } else {
      // Otherwise just go back one step
      this.appState = this.appState.prevStep();
    }
  };

  private renderContent() {
    const currentStep = this.appState.currentStep;
    const isStepInvalid = currentStep === -1;

    if (this.appState.loadingState.isFailed) {
      return this.renderError();
    }

    if (isStepInvalid) {
      return '';
    }

    return html`
      ${this.renderBackButton()}
      ${this.renderStep()}
    `;
  }

  private renderError() {
    return html`
      <div class="ents-or__error">
        <p class="ents-copy ents-or__hint ents-or__hint--error">
          ${this.config.messages.error}
        </p>
      </div>
    `;
  }

  private renderStep() {
    const { currentStep } = this.appState;

    switch (currentStep) {
      case 0:
        return this.renderStepOne();
      case 1:
        return this.renderStepTwo();
      case 2:
        return this.renderStepThree();
      case 3:
        return this.renderStepFour();
      case 4:
        return this.renderStepFive();
      default:
        return '';
    }
  }

  private renderStepOne() {
    const { article, skipSizeLayer } = this.modelState;

    const orInformation = new ORInformation(
      this.messages,
      () => {
        if (skipSizeLayer) {
          this.appState = this.appState.goToStoreSelectionStep();
        } else {
          this.appState = this.appState.nextStep();
        }
      },
      article,
      skipSizeLayer
    );

    return orInformation.renderStep();
  }

  private renderStepTwo() {
    const { sizesWithStoreStock } = this.modelState;

    const orSize = new ORSize(
      this.messages,
      () => this.appState = this.appState.nextStep(),
      (articleId: string) => this.selectionState = this.selectionState.selectSize(articleId),
      sizesWithStoreStock
    );

    return orSize.renderStep();
  }

  private renderStepThree() {
    const { selectedSize } = this.selectionState;
    const { skipSizeLayer } = this.modelState;

    const orStore = new ORStore (
      this.messages,
      () => this.appState = this.appState.nextStep(),
      (store: Store) => {
        this.selectionState = this.selectionState.selectStore(store);
        this.selectionState.saveSelectedStoreId();
      },
      this.modelState.getSizeById(selectedSize),
      !skipSizeLayer,
      this.modelState.getStoresForSize(selectedSize)
    );

    return orStore.renderStep();
  }

  private renderStepFour() {
    const { selectedSize, selectedStore } = this.selectionState;
    const { skipSizeLayer } = this.modelState;
    const size = this.modelState.getSizeById(selectedSize);
    const config = {
      articleId: selectedSize,
      size: size,
      storeLocation: selectedStore,
      dataProtectionRegulationLink: this.config.dataProtectionRegulationLink,
      reservationEndpoint: this.config.reservationEndpoint
    };
    const params: ORFormParams = {
      handleInputChange: e => this.handleInputChange(e),
      handleRadioChange: salutation => this.handleRadioChange(salutation),
      validateField: e => this.validateField(e),
      validateEmail: e => this.validateEmail(e),
      submitReservation: () => this.submitReservation()
    };

    const orForm = new ORForm (
      this.messages,
      config,
      this.stepFourState,
      !skipSizeLayer,
      params
    );

    return orForm.renderStep();
  }

  private renderStepFive() {
    const { reservationSuccessful } = this.appState;
    const { selectedSize, selectedStore } = this.selectionState;
    const { article, skipSizeLayer } = this.modelState;
    const size = this.modelState.getSizeById(selectedSize);

    const orResult = new ORResult(
      this.messages,
      () => this.hideDrawer(),
      () => this.handleReservationRetry(),
      {
        reservationSuccessful: reservationSuccessful === true,
        article: article,
        storeLocationName: selectedStore.name,
        sizeDescription: size,
        showSizeBadge: !skipSizeLayer,
      }
    );

    return orResult.renderStep();
  }

  private resetState() {
    this.appState = AppState.reset();
    this.selectionState = SelectionState.reset();
    this.stepFourState = createInitialStepFourState();
  }

  private showDrawer() {
    this.resetState();
    this.appState = this.appState.nextStep();
    return this.doOnDrawer(drawer => drawer.show());
  }

  private hideDrawer() {
    this.resetState();
    return this.doOnDrawer(drawer => drawer.hide());
  }

  private doOnDrawer(action: (drawer: EntsDrawer) => void) {
    const drawer = this.shadowRoot?.querySelector<EntsDrawer>('#onlineReservationDrawer');
    if (drawer) {
      action(drawer);
      return true;
    }
    return false;
  }

  private getTrackingData(): DomainEvent {
    return {
      event_name: 'click_reserve',
      event_data: {
        reservation_store_name: this.selectionState.selectedStore?.name || ''
      },
      currency: {
        code: 'EUR'
      },
      items: [
        {
          produkt_id: this.modelState.trackingData.produkt_id,
          farb_id: this.modelState.trackingData.farb_id ?? '',
          artikel_id: this.selectionState.selectedSize,
          vertriebsinfo_id: this.modelState.getsalesInformationIdById(this.selectionState.selectedSize),
          syan: this.modelState.trackingData.syan,
          quantity: 1
        }
      ],
      responsible_team_tracking: "ENTS",
      responsible_team_feature: "ENTS",
    };
  }

  handleSuccessfulReservation() {
    this.appState = this.appState.setReservationStatus(true);
    track(this.getTrackingData());
    this.appState = this.appState.nextStep();
  }

  handleFailedReservation(errorMessage: string) {
    console.error(errorMessage);
    this.appState = this.appState.setReservationStatus(false);
    this.appState = this.appState.nextStep();
  }

  handleReservationRetry() {
    this.appState = this.appState.prevStep();
  }
  private handleInputChange(event: Event) {
    const target = event.target as HTMLElement;
    let name: string | null | undefined;
    let value: string | boolean | null | undefined;

    // Handle BDK text field component
    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as HTMLInputElement;
      name = bdkField.getAttribute('name') ?? '';
      value = bdkField.value;
    }
    // Handle BDK checkbox component
    else if (target.tagName === 'BDK-ENTS-CHECKBOX') {
      const bdkCheckbox = target as HTMLInputElement;
      name = bdkCheckbox.name || '';
      value = bdkCheckbox.checked;
    }

    if (!name) {
      return;
    }

    const formData = {
      ...this.stepFourState.formData,
      [name]: value
    };

    // Clear error when user starts typing
    let fieldErrors = {...this.stepFourState.fieldErrors};
    if (fieldErrors[name]) {
      fieldErrors = {
        ...fieldErrors,
        [name]: false
      };
    }

    this.stepFourState = {
      formData,
      fieldErrors
    };
  }

  private handleRadioChange(salutation: 'ms' | 'mr') {
    this.stepFourState = {
      ...this.stepFourState,
      formData: {
        ...this.stepFourState.formData,
        salutation
      }
    };
  }

  private validateField(event: Event) {
    const target = event.target as HTMLElement;
    let name: string;
    let value: string;

    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as HTMLInputElement;
      name = bdkField.getAttribute('name') ?? '';
      value = bdkField.value;
    } else {
      name = '';
      value = '';
    }

    if (name in this.stepFourState.fieldErrors) {
      const isValid = value.trim() !== '';

      this.stepFourState = {
        ...this.stepFourState,
        fieldErrors: {
          ...this.stepFourState.fieldErrors,
          [name]: !isValid
        }
      };
    }
  }

  private validateEmail(event: Event) {
    const target = event.target as HTMLElement;
    let value = '';

    // Handle BDK text field component - get the value properly
    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as HTMLInputElement;
      value = bdkField.value || '';
    } else {
      // Handle input events from shadow DOM
      const input = target as HTMLInputElement;
      if (input.value !== undefined) {
        value = input.value;
      } else {
        // Try to find the input inside shadow DOM if possible
        const bdkTextFieldHost = (input.getRootNode() as ShadowRoot)?.host;
        if (bdkTextFieldHost?.tagName === 'BDK-ENTS-TEXT-FIELD') {
          const bdkField = bdkTextFieldHost as HTMLInputElement;
          value = bdkField.value || '';
        }
      }
    }

    // Apply validation and update state
    const isInvalid = validateEmailValue(value);

    this.stepFourState = {
      ...this.stepFourState,
      fieldErrors: {
        ...this.stepFourState.fieldErrors,
        email: isInvalid
      }
    };
  }

  private validateAllFields(): boolean {
    const { formData } = this.stepFourState;

    // Make sure to call the validateEmailValue function correctly
    const newErrors = {
      firstName: formData.firstName.trim() === '',
      lastName: formData.lastName.trim() === '',
      email: validateEmailValue(formData.email)
    };

    this.stepFourState = {
      ...this.stepFourState,
      fieldErrors: newErrors
    };

    return !Object.values(newErrors).some(isError => isError);
  }

  private submitReservation() {
    const isValid = this.validateAllFields();

    if (isValid) {
      const { formData } = this.stepFourState;
      const apiRequestData = {
        artikelListe: [
          {
            artikelNummer: this.selectionState.selectedSize
          }
        ],
        haus: {
          nummer: this.selectionState.selectedStore.storeId
        },
        kundenKontakt: {
          anrede: formData.salutation === 'ms' ? this.messages.salutationMs : this.messages.salutationMr,
          vorname: formData.firstName,
          nachname: formData.lastName,
          email: formData.email
        },
        newsletteranmeldung: {
          newsletteranmeldung: formData.newsletter,
          labelText: this.messages.newsletter
        }
      };

      // Make the API request
      fetch(this.config.reservationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiRequestData)
      })
        .then((response) => {
          if (response.ok) {
            // Status code 200 - Reservation successful
            this.handleSuccessfulReservation();
          } else {
            // Other error
            this.handleFailedReservation('online-reservation request failed!');
          }
        })
        .catch((error) => {
          console.error('Reservation request failed:', error);
          this.handleFailedReservation('');
        });
    }
  }

}
